import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import HomeHero from "../components/heroes/HomeHero";
import AccentButton from "../components/ui/AccentButton";

const revealVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1] },
  },
};

// --- 1. БЛОК FEATURES ---
const Features = () => {
  const { t } = useTranslation();

  const features = [
    {
      title: t("home.features.list.booking_title"),
      desc: t("home.features.list.booking_desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 21C14.5 21 15.5 16.9706 15.5 12C15.5 7.02944 14.5 3 12 3C9.5 3 8.5 7.02944 8.5 12C8.5 16.9706 9.5 21 12 21Z"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3 12H21"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: t("home.features.list.profiles_title"),
      desc: t("home.features.list.profiles_desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M18.8 19.3333L16.2 16M16.2 16C16.8 15.4667 17 14.8667 17 14.1333C17 12.3333 16 11.2667 15.2667 10.4C14.8 9.8 14.2667 9.2667 13.8 8.66667C13.2 8.13333 12.6 7.33333 12.6 6.13333C12.6 4.33333 14 3.26667 14.6 2.6L16.4 7.6L18.8 19.3333ZM16.2 16H8.2M8.2 16C7.6 15.4667 7.4 14.8667 7.4 14.1333C7.4 12.3333 8.4 11.2667 9.13333 10.4C9.6 9.8 10.1333 9.2667 10.6 8.66667C11.2 8.13333 11.8 7.33333 11.8 6.13333C11.8 4.33333 10.4 3.26667 9.8 2.6L8 7.6L8.2 16Z"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: t("home.features.list.calendar_title"),
      desc: t("home.features.list.calendar_desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5Z"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 3V7"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8 3V7"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3 11H21"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: t("home.features.list.insights_title"),
      desc: t("home.features.list.insights_desc"),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M3 3V21H21"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 16L11 11L14 14L19 8"
            stroke="#F65814"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  return (
    <section className="bg-white py-24 px-10 md:px-16 lg:px-24 font-['Inter']">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={revealVariants}
        className="max-w-6xl mx-auto text-center mb-16 relative"
      >
        <h2 className="text-[30px] md:text-[48px] font-[900] text-black tracking-tight leading-[1.1] mb-4">
          {t("home.features.headline")}
        </h2>

        <p className="text-slate-500 text-base md:text-lg max-w-2xl mx-auto font-medium">
          {t("home.features.subheadline")}
        </p>
      </motion.div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="bg-white border border-slate-100 p-8 rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.03)] hover:scale-[1.03] transition-all"
          >
            <div className="bg-[#FFF8F4] p-5 rounded-3xl mb-8 inline-block border border-[#F65814]/10 shadow-[0_12px_24px_rgba(246,88,20,0.1)]">
              {feature.icon}
            </div>

            <h3 className="text-lg font-[900] text-black mb-4 tracking-tight">
              {feature.title}
            </h3>

            <p className="text-slate-500 text-sm leading-relaxed font-medium">
              {feature.desc}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// --- 2. БЛОК MODERN PET BUSINESS ---
const ModernPetBusiness = () => {
  const { t } = useTranslation();

  const cards = [
    {
      title: t("home.modern_biz.cards.grooming"),
      category: t("home.modern_biz.cards.grooming_cat"),
      image:
        "https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?q=80&w=500&auto=format&fit=crop",
      rotate: "-6deg",
      zIndex: 10,
      translateY: "20px",
    },
    {
      title: t("home.modern_biz.cards.daycare"),
      category: t("home.modern_biz.cards.daycare_cat"),
      image: "/images/daycare.jpg",
      rotate: "0deg",
      zIndex: 20,
      translateY: "0px",
    },
    {
      title: t("home.modern_biz.cards.hotel"),
      category: t("home.modern_biz.cards.hotel_cat"),
      image:
        "https://images.unsplash.com/photo-1591946614720-90a587da4a36?q=80&w=500&auto=format&fit=crop",
      rotate: "6deg",
      zIndex: 10,
      translateY: "20px",
    },
  ];

  return (
    <section className="bg-white py-24 px-10 md:px-16 lg:px-24 overflow-hidden font-['Inter']">
      <div className="max-w-5xl mx-auto flex flex-row justify-center items-end h-[380px] mb-20 relative">
        {cards.map((card) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 100, rotate: 0 }}
            whileInView={{
              opacity: 1,
              y: card.translateY,
              rotate: card.rotate,
            }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: 0.1, duration: 0.8, ease: "easeOut" }}
            style={{ zIndex: card.zIndex }}
            className="w-[290px] bg-white rounded-[32px] p-4 shadow-[0_30px_60px_rgba(0,0,0,0.12)] border border-slate-50 mx-[-40px]"
          >
            <div className="rounded-[24px] overflow-hidden h-[220px] mb-6">
              <img
                src={card.image}
                alt={card.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="px-2 pb-4">
              <p className="text-[#F65814] font-[900] uppercase tracking-[2px] text-[9px] mb-2">
                {card.category}
              </p>

              <h3 className="text-lg font-[900] text-black tracking-tight">
                {card.title}
              </h3>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={revealVariants}
        className="max-w-6xl mx-auto text-center"
      >
        <h2 className="text-[34px] md:text-[48px] font-[900] text-black tracking-tight leading-[1.1] mb-4">
          {t("home.modern_biz.headline")}
        </h2>

        <p className="text-slate-500 text-base md:text-lg max-w-2xl mx-auto font-medium">
          {t("home.modern_biz.subheadline")}
        </p>
      </motion.div>
    </section>
  );
};

// --- 3. БЛОК HOW IT WORKS ---
const HowItWorks = () => {
  const { t } = useTranslation();

  const steps = [
    {
      title: t("home.how_it_works.steps.step1_title"),
      desc: t("home.how_it_works.steps.step1_desc"),
    },
    {
      title: t("home.how_it_works.steps.step2_title"),
      desc: t("home.how_it_works.steps.step2_desc"),
    },
    {
      title: t("home.how_it_works.steps.step3_title"),
      desc: t("home.how_it_works.steps.step3_desc"),
    },
  ];

  return (
    <section className="bg-white py-24 px-10 md:px-16 lg:px-24 font-['Inter']">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={revealVariants}
          className="lg:sticky lg:top-32"
        >
          <h2 className="text-[44px] md:text-[58px] font-[900] text-black tracking-tight leading-[1.1] mb-6">
            {t("home.how_it_works.headline")}
          </h2>

          <p className="text-slate-500 text-base md:text-lg max-w-md font-medium leading-relaxed">
            {t("home.how_it_works.subheadline")}
          </p>
        </motion.div>

        <div className="space-y-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.7 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              className="relative pl-8 border-l-2 border-slate-100 group"
            >
              <div className="absolute left-[-2px] top-0 bottom-0 w-[2px] bg-[#F65814] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top" />

              <div className="text-[#F65814] font-[900] text-[11px] tracking-[2px] mb-4 uppercase">
                {t("home.how_it_works.step")} 0{index + 1}
              </div>

              <h3 className="text-[26px] md:text-[32px] font-[900] text-black tracking-tight mb-4 leading-tight">
                {step.title}
              </h3>

              <p className="text-slate-500 text-base md:text-lg font-medium leading-relaxed max-w-xl">
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- 4. БЛОК PRICING ---
const Pricing = () => {
  const { t } = useTranslation();

  const [index, setIndex] = useState(0);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  const formatPrice = (amount, currency) => {
    const cents = Number(amount || 0);
    const value = cents / 100;
    const currencyCode = String(currency || "usd").toUpperCase();

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatInterval = (interval) => {
    const normalized = String(interval || "month").toLowerCase();

    switch (normalized) {
      case "day":
        return "/ day";
      case "week":
        return "/ week";
      case "year":
        return "/ year";
      case "month":
      default:
        return t("home.pricing.per_month");
    }
  };

  const normalizePlan = (plan) => {
    const key = String(
      plan?.key ||
        plan?.plan_key ||
        plan?.metadata?.plan_key ||
        plan?.metadata?.key ||
        plan?.nickname ||
        plan?.name ||
        plan?.price_id ||
        plan?.id ||
        "",
    ).toLowerCase();

    const getPlanTag = (key) => {
      const normalizedKey = String(key || "").toLowerCase();
    
      switch (normalizedKey) {
        case "basic":
        case "starter":
          return t("home.pricing.plans.basic_tag");
    
        case "business":
        case "pro":
          return t("home.pricing.plans.business_tag");
    
        case "enterprise":
          return t("home.pricing.plans.enterprise_tag");
    
        default:
          return "";
      }
    };

    return {
      key: key || crypto.randomUUID(),
      name: plan?.name || key || "Plan",
      description: plan?.description || "",
      priceLabel: formatPrice(plan?.amount || plan?.unit_amount || 0, plan?.currency),
      intervalLabel: formatInterval(plan?.interval || plan?.recurring?.interval),
      priceId: plan?.price_id || plan?.id || "",
    };
  };

  useEffect(() => {
    let isMounted = true;

    const loadStripePlans = async () => {
      try {
        setPlansLoading(true);

        const response = await fetch("/api/v1/billing/plans");
        const data = await response.json();

        console.log("HOME STRIPE PLANS RESPONSE:", data);

        if (!isMounted) {
          return;
        }

        if (data?.success && Array.isArray(data.plans)) {
          const order = {
            basic: 1,
            starter: 1,
            pro: 2,
            business: 2,
            enterprise: 3,
          };

          const normalizedPlans = data.plans
            .map(normalizePlan)
            .filter((plan) => plan.priceLabel && plan.name)
            .sort((a, b) => (order[a.key] || 99) - (order[b.key] || 99));

          setPlans(normalizedPlans);
        } else {
          setPlans([]);
        }
      } catch (error) {
        console.error("Load Stripe plans error:", error);

        if (isMounted) {
          setPlans([]);
        }
      } finally {
        if (isMounted) {
          setPlansLoading(false);
        }
      }
    };

    loadStripePlans();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!plans.length) {
      return undefined;
    }

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % plans.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [plans.length]);

  useEffect(() => {
    if (index >= plans.length) {
      setIndex(0);
    }
  }, [index, plans.length]);

  const currentPlan = plans[index];

  return (
    <section className="bg-white py-24 px-10 md:px-16 lg:px-24 font-['Inter']">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="max-w-6xl mx-auto bg-black rounded-[40px] md:rounded-[60px] p-8 md:p-16 relative overflow-hidden"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center relative z-10">
          <div className="flex flex-col items-start lg:pr-10 text-white">
            <h2 className="text-[34px] md:text-[54px] font-[900] leading-[1.1] mb-6 tracking-tight">
              {t("home.pricing.headline")}
            </h2>

            <p className="text-slate-400 text-base md:text-lg mb-10 max-w-md font-medium leading-relaxed">
              {t("home.pricing.subheadline")}
            </p>

            <AccentButton to="/pricing">
              {plansLoading ? "Loading Plans..." : t("home.pricing.btn_start")}
            </AccentButton>
          </div>

          <div className="relative w-full max-w-[400px] h-[420px] flex items-center justify-center lg:ml-auto">
            {plansLoading && (
              <div className="bg-[#0D0D0D] rounded-[48px] p-10 md:p-12 border border-white/10 w-full shadow-2xl">
                <div className="h-8 w-32 rounded-full bg-white/10 animate-pulse mb-8" />
                <div className="h-12 w-44 rounded-full bg-white/10 animate-pulse mb-6" />
                <div className="h-16 w-36 rounded-full bg-white/10 animate-pulse" />
              </div>
            )}

            {!plansLoading && !plans.length && (
              <div className="bg-[#0D0D0D] rounded-[48px] p-10 md:p-12 border border-white/10 w-full shadow-2xl text-center">
                <h3 className="text-3xl md:text-4xl font-[900] text-white tracking-tighter">
                  No plans available
                </h3>

                <p className="text-slate-500 text-sm font-bold mt-4">
                  Stripe plans are not available right now.
                </p>
              </div>
            )}

            {!plansLoading && currentPlan && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPlan.key}
                  initial={{ opacity: 0, scale: 0.98, filter: "blur(8px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 1.02, filter: "blur(8px)" }}
                  className="bg-[#0D0D0D] rounded-[48px] p-10 md:p-12 border border-white/10 w-full shadow-2xl"
                >
                  <div className="border-b border-white/5 pb-8 mb-8">
                    <h3 className="text-3xl md:text-4xl font-[900] text-white tracking-tighter">
                      {currentPlan.name}
                    </h3>
                    {currentPlan.tag && (
                      <p className="text-[#F65814] font-[900] uppercase text-[9px] mt-3 tracking-[2px]">
                        {currentPlan.tag}
                      </p>
                    )}
                  </div>

                  <div className="mb-10">
                    <p className="text-5xl md:text-6xl font-[900] text-white tracking-tighter">
                      {currentPlan.priceLabel}
                    </p>

                    <p className="text-slate-500 text-[10px] font-bold uppercase mt-3 tracking-[2px]">
                      {currentPlan.intervalLabel}
                    </p>
                    
                    {currentPlan.description && (
                    <p className="text-[#F65814] font-[900] uppercase text-[9px] mt-3 tracking-[2px] leading-relaxed">
                      {currentPlan.description}
                    </p>
                    )}

                  </div>

                  <AccentButton to="/pricing" className="w-full">
                    {t("home.pricing.trial_btn")}
                  </AccentButton>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// --- ГЛАВНАЯ СТРАНИЦА ---
export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <HomeHero />
      <Features />
      <ModernPetBusiness />
      <HowItWorks />
      <Pricing />
    </div>
  );
}